import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Alert, AlertIcon, Badge, Box, Button, Checkbox, Flex, FormLabel, Grid, GridItem,
  Heading, HStack, Input, Radio, RadioGroup, Select, Spinner, Stack, Table, Thead, Tbody, Tr, Th, Td, Text
} from '@chakra-ui/react'
import {
  calculateCrate, edgeQuoteFormToCrateInputs, EMC_FACES,
  isUtsEligibleShipping, carrierToUtsFormFields, EMPTY_UTS_FIELDS
} from 'lib'
import { getUtsConfig, getUtsRateEstimate } from 'api'

const formatCurrency = (value) => {
  if (value == null || isNaN(Number(value))) return '—'
  return Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

const formatDate = (value) => {
  if (!value) return '—'
  const d = new Date(value)
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US')
}

export const EdgeCrateResultsForm = ({ form, setForm, ...rest }) => {
  const [arrangement, setArrangement] = useState('stacked')
  const showUtsPanel = isUtsEligibleShipping(form.shippingOption)

  const hasEmc = !form.noEmc && form.emcModsH > 0 && form.emcModsL > 0
  const hasCabinet = !form.noCabinet && form.cabinetHeightIn > 0 && form.cabinetLengthIn > 0
  const showArrangement = hasEmc && hasCabinet

  const result = useMemo(() => {
    const inputs = edgeQuoteFormToCrateInputs(form, { EMC_FACES })
    inputs.arrangement = arrangement
    return calculateCrate(inputs)
  }, [
    form.noEmc, form.emcModsH, form.emcModsL, form.emcFace,
    form.noCabinet, form.cabinetHeightIn, form.cabinetLengthIn,
    form.noPedestal, form.pedestalHeightIn, form.pedestalLengthIn,
    form.noMonument, form.monumentStyle, form.monumentLengthIn, form.monumentHeightIn,
    arrangement
  ])

  const hasResults = result.crates.length > 0

  const [originZip, setOriginZip] = useState('')
  const [destinationZip, setDestinationZip] = useState(form.customer?.zip || '')
  const [paymentType, setPaymentType] = useState('PREPAID')
  const [includeGuaranteed, setIncludeGuaranteed] = useState(false)
  const [freightLoading, setFreightLoading] = useState(false)
  const [freightError, setFreightError] = useState('')
  const [freightResponse, setFreightResponse] = useState(null)
  const [selectedCarrierKey, setSelectedCarrierKey] = useState('')

  useEffect(() => {
    let cancelled = false
    getUtsConfig().then((cfg) => {
      if (cancelled) return
      if (cfg?.originZip) setOriginZip(prev => prev || cfg.originZip)
      if (cfg?.paymentType) setPaymentType(prev => prev || cfg.paymentType)
    }).catch(() => {})
    return () => { cancelled = true }
  }, [])

  const customerId = form.customer?.id
  const customerZip = form.customer?.zip
  useEffect(() => {
    if (customerZip) setDestinationZip(customerZip)
  }, [customerId, customerZip])

  // Clear UTS state when shipping option is not eligible for freight API.
  useEffect(() => {
    if (!showUtsPanel) {
      setFreightResponse(null)
      setFreightError('')
      setSelectedCarrierKey('')
      if (setForm && form.utsCarrierName) {
        setForm(draft => { Object.assign(draft, EMPTY_UTS_FIELDS) })
      }
    }
  }, [showUtsPanel, setForm, form.utsCarrierName])

  const anyOversized = result.crates.some(c => c.oversized)
  const anyMissingClass = result.crates.some(c => !c.freightClass)
  const canFetchFreight = showUtsPanel && hasResults && destinationZip && !anyMissingClass

  const handleFetchFreight = async () => {
    setFreightLoading(true)
    setFreightError('')
    setFreightResponse(null)
    setSelectedCarrierKey('')
    try {
      const data = await getUtsRateEstimate({
        crates: result.crates.map(c => ({
          lengthFt: c.lengthFt,
          widthIn: c.widthIn,
          heightFt: c.heightFt,
          totalWeightLb: c.totalWeightLb,
          freightClass: c.freightClass
        })),
        originZip: originZip || undefined,
        destinationZip,
        paymentType,
        includeGuaranteedRates: includeGuaranteed
      })
      setFreightResponse(data)
    } catch (err) {
      const body = err.response?.data
      const details = body?.details
      const serviceMsgs = details?.serviceMessages || body?.serviceMessages
      const detailText = Array.isArray(serviceMsgs) && serviceMsgs.length
        ? serviceMsgs.map(m => `${m.severity || 'INFO'}: ${m.message}`).join(' | ')
        : (details?.message || details ? (typeof details === 'string' ? details : JSON.stringify(details)) : '')
      const baseMsg = body?.error || body?.message || err.message || 'Failed to fetch UTS rate estimate.'
      setFreightError(detailText ? `${baseMsg} — ${detailText}` : baseMsg)
    } finally {
      setFreightLoading(false)
    }
  }

  const carrierRates = freightResponse?.CarrierRates || freightResponse?.carrierRates || []
  const sortedCarrierRates = [...carrierRates].sort((a, b) => {
    const aTotal = Number(a.charges?.totalAmount) || Infinity
    const bTotal = Number(b.charges?.totalAmount) || Infinity
    return aTotal - bTotal
  })
  const topCarrierRates = sortedCarrierRates.slice(0, 3)

  const carrierKey = (c, idx) => `${c.utsScac || c.nationalScac || 'carrier'}-${idx}`

  const handleSelectCarrier = (key) => {
    setSelectedCarrierKey(key)
    const idx = topCarrierRates.findIndex((c, i) => carrierKey(c, i) === key)
    const carrier = topCarrierRates[idx]
    if (!carrier || !setForm) return
    const utsFields = carrierToUtsFormFields(carrier, freightResponse?.estimateNumber || '')
    setForm(draft => {
      draft.shippingCost = parseFloat((utsFields.utsRateAmount || 0).toFixed(2))
      draft.utsCarrierName = utsFields.utsCarrierName
      draft.utsCarrierScac = utsFields.utsCarrierScac
      draft.utsServiceLevel = utsFields.utsServiceLevel
      draft.utsEstimateNumber = utsFields.utsEstimateNumber
      draft.utsTransitDays = utsFields.utsTransitDays
      draft.utsRateAmount = utsFields.utsRateAmount
    })
  }

  return (
    <Box {...rest}>
      <Heading size="md" mb={4}>Crate Estimate</Heading>
      <Grid templateColumns="1fr" gap={2} w="900px" maxW="100%">
        <GridItem>
          {hasResults ? (
            <Flex direction="column" gap={4} p={4} bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.200">

              {result.requiresEngineeredSplit && (
                <Alert status="warning" fontSize="sm">
                  <AlertIcon />
                  One or more fabricated components exceed standard crate limits. An engineered split or custom crate is required — the calculator will not auto-split fabricated signage.
                </Alert>
              )}

              {showArrangement && (
                <Flex align="center" gap={3} mb={2}>
                  <FormLabel mb={0} fontWeight="bold" w="200px" flexShrink={0}>EMC / Cabinet Layout</FormLabel>
                  <Select
                    size="sm"
                    maxW="200px"
                    value={arrangement}
                    onChange={e => setArrangement(e.target.value)}
                  >
                    <option value="stacked">Stacked</option>
                    <option value="side-by-side">Side by Side</option>
                  </Select>
                </Flex>
              )}

              {result.emcSections.length > 0 && (
                <Box>
                  <Heading size="sm" mb={2}>EMC Shipping Sections</Heading>
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Section</Th>
                        <Th>Size</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {result.emcSections.map((s, idx) => (
                        <Tr key={`${s.label}-${idx}`}>
                          <Td>{s.label}</Td>
                          <Td>{s.heightFt.toFixed(2)}&apos; H &times; {s.lengthFt.toFixed(2)}&apos; L</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}

              <Box>
                <Heading size="sm" mb={2}>Component Weights</Heading>
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Component</Th>
                      <Th>Size</Th>
                      <Th isNumeric>Weight (lb)</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {result.components.map(c => (
                      <Tr key={c.type}>
                        <Td>{c.label}</Td>
                        <Td>{c.heightFt.toFixed(2)}&apos; H &times; {c.lengthFt.toFixed(2)}&apos; L</Td>
                        <Td isNumeric>{c.weight.toFixed(1)}</Td>
                      </Tr>
                    ))}
                    <Tr fontWeight="bold">
                      <Td>Total Product Weight</Td>
                      <Td />
                      <Td isNumeric>{result.totalProductWeight.toFixed(1)}</Td>
                    </Tr>
                  </Tbody>
                </Table>
              </Box>

              <Box>
                <Heading size="sm" mb={2}>
                  Crate Details
                  {result.crates.length > 1 && (
                    <Badge ml={2} colorScheme="orange" fontSize="0.75em">{result.crates.length} Crates</Badge>
                  )}
                </Heading>

                {result.crates.map(crate => (
                  <Box
                    key={crate.number}
                    mb={3}
                    p={3}
                    bg={crate.oversized ? 'orange.50' : 'white'}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={crate.oversized ? 'orange.300' : 'gray.200'}
                  >
                    <Flex align="center" gap={2} mb={2} flexWrap="wrap">
                      <Text fontWeight="bold" fontSize="sm">
                        Crate {crate.number}{result.crates.length > 1 ? ` — ${crate.contents.join(', ')}` : ''}
                      </Text>
                      {crate.requiresEngineeredSplit && (
                        <Badge colorScheme="red" fontSize="0.7em">Engineered Split Required</Badge>
                      )}
                      {crate.oversized && !crate.requiresEngineeredSplit && (
                        <Badge colorScheme="orange" fontSize="0.7em">Oversized — Custom Crate Required</Badge>
                      )}
                    </Flex>
                    <Grid templateColumns="repeat(3, 1fr)" gap={2}>
                      <GridItem>
                        <Text fontSize="xs" color="gray.500" fontWeight="bold">Dimensions</Text>
                        <Text fontSize="sm">{crate.lengthFt}&apos; L &times; {crate.widthIn}&quot; W &times; {crate.heightFt}&apos; H</Text>
                      </GridItem>
                      <GridItem>
                        <Text fontSize="xs" color="gray.500" fontWeight="bold">Crating Weight</Text>
                        <Text fontSize="sm">{crate.cratingWeightLb.toFixed(1)} lb</Text>
                      </GridItem>
                      <GridItem>
                        <Text fontSize="xs" color="gray.500" fontWeight="bold">Product Weight</Text>
                        <Text fontSize="sm">{crate.productWeightLb.toFixed(1)} lb</Text>
                      </GridItem>
                      <GridItem>
                        <Text fontSize="xs" color="gray.500" fontWeight="bold">Total Weight</Text>
                        <Text fontSize="sm" fontWeight="bold">{crate.totalWeightLb.toFixed(1)} lb</Text>
                      </GridItem>
                      <GridItem>
                        <Text fontSize="xs" color="gray.500" fontWeight="bold">Density</Text>
                        <Text fontSize="sm">{crate.density.toFixed(2)} lb/cu ft</Text>
                      </GridItem>
                      <GridItem>
                        <Text fontSize="xs" color="gray.500" fontWeight="bold">Freight Class</Text>
                        <Text fontSize="sm">{crate.freightClass ? `Class ${crate.freightClass}` : '—'}</Text>
                      </GridItem>
                    </Grid>
                  </Box>
                ))}
              </Box>

              {result.crates.length > 1 && (
                <Flex justify="flex-end" gap={6} pt={2} borderTopWidth="1px" borderColor="gray.200">
                  <Text fontSize="sm"><Text as="span" fontWeight="bold">Total Crating:</Text> {result.totalCratingWeight.toFixed(1)} lb</Text>
                  <Text fontSize="sm"><Text as="span" fontWeight="bold">Total Weight:</Text> {result.totalWeight.toFixed(1)} lb</Text>
                </Flex>
              )}

              {showUtsPanel && (
                <Box pt={3} borderTopWidth="1px" borderColor="gray.200">
                  <Heading size="sm" mb={3}>UTS Freight Estimate</Heading>

                  <Grid templateColumns="repeat(4, 1fr)" gap={3} mb={3}>
                    <GridItem>
                      <FormLabel fontSize="xs" mb={1}>Origin Zip</FormLabel>
                      <Input
                        size="sm"
                        value={originZip}
                        onChange={e => setOriginZip(e.target.value)}
                        placeholder="GRS warehouse zip"
                      />
                    </GridItem>
                    <GridItem>
                      <FormLabel fontSize="xs" mb={1}>Destination Zip</FormLabel>
                      <Input
                        size="sm"
                        value={destinationZip}
                        onChange={e => setDestinationZip(e.target.value)}
                        placeholder="e.g. 30303"
                      />
                    </GridItem>
                    <GridItem>
                      <FormLabel fontSize="xs" mb={1}>Payment Type</FormLabel>
                      <Select size="sm" value={paymentType} onChange={e => setPaymentType(e.target.value)}>
                        <option value="PREPAID">Prepaid</option>
                        <option value="COLLECT">Collect</option>
                        <option value="THIRD_PARTY">Third Party</option>
                        <option value="PREPAID_ADD">Prepaid + Add</option>
                      </Select>
                    </GridItem>
                    <GridItem display="flex" alignItems="flex-end">
                      <Checkbox
                        isChecked={includeGuaranteed}
                        onChange={e => setIncludeGuaranteed(e.target.checked)}
                        size="sm"
                      >
                        Include Guaranteed Rates
                      </Checkbox>
                    </GridItem>
                  </Grid>

                  <HStack gap={3} mb={3}>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={handleFetchFreight}
                      isDisabled={!canFetchFreight || freightLoading}
                    >
                      {freightLoading ? <Spinner size="xs" mr={2} /> : null}
                      Get Freight Estimate
                    </Button>
                    {anyOversized && (
                      <Text fontSize="xs" color="orange.700">
                        Oversized crates may require manual quoting; LTL rates may not apply.
                      </Text>
                    )}
                    {anyMissingClass && (
                      <Text fontSize="xs" color="red.600">
                        One or more crates is missing a freight class; cannot fetch rates.
                      </Text>
                    )}
                  </HStack>

                  {freightError && (
                    <Alert status="error" mb={3} fontSize="sm">
                      <AlertIcon />
                      {freightError}
                    </Alert>
                  )}

                  {freightResponse && (
                    <Box>
                      {(freightResponse.origin || freightResponse.destination) && (
                        <Flex gap={6} mb={2} fontSize="xs" color="gray.600" flexWrap="wrap">
                          {freightResponse.origin && (
                            <Text>
                              <Text as="span" fontWeight="bold">Origin:</Text>{' '}
                              {freightResponse.origin.city}, {freightResponse.origin.state} {freightResponse.origin.zip}
                            </Text>
                          )}
                          {freightResponse.destination && (
                            <Text>
                              <Text as="span" fontWeight="bold">Destination:</Text>{' '}
                              {freightResponse.destination.city}, {freightResponse.destination.state} {freightResponse.destination.zip}
                            </Text>
                          )}
                          {freightResponse.estimateNumber && (
                            <Text><Text as="span" fontWeight="bold">Estimate #:</Text> {freightResponse.estimateNumber}</Text>
                          )}
                        </Flex>
                      )}

                      {topCarrierRates.length === 0 ? (
                        <Text fontSize="sm" color="gray.600">No carrier rates returned.</Text>
                      ) : (
                        <Box>
                          <Text fontSize="sm" fontWeight="bold" mb={2}>Top 3 carriers (lowest price) — select one to apply shipping cost</Text>
                          <RadioGroup value={selectedCarrierKey} onChange={handleSelectCarrier}>
                            <Stack spacing={2}>
                              {topCarrierRates.map((c, idx) => {
                                const key = carrierKey(c, idx)
                                return (
                                  <Box
                                    key={key}
                                    p={3}
                                    borderWidth="1px"
                                    borderRadius="md"
                                    borderColor={selectedCarrierKey === key ? 'blue.400' : 'gray.200'}
                                    bg={selectedCarrierKey === key ? 'blue.50' : 'white'}
                                  >
                                    <Radio value={key} width="100%">
                                      <Flex justify="space-between" align="center" w="100%" gap={4} flexWrap="wrap">
                                        <HStack spacing={2}>
                                          <Badge colorScheme="purple" fontSize="0.65em">#{idx + 1}</Badge>
                                          <Text fontWeight="bold">{c.carrierName}</Text>
                                          {c.isBestValueCarrier && (
                                            <Badge colorScheme="green" fontSize="0.65em">Best Value</Badge>
                                          )}
                                        </HStack>
                                        <Text fontWeight="bold">{formatCurrency(c.charges?.totalAmount)}</Text>
                                      </Flex>
                                    </Radio>
                                    <Grid templateColumns="repeat(4, 1fr)" gap={2} mt={2} ml={6} fontSize="xs" color="gray.600">
                                      <Text>SCAC: {c.utsScac || c.nationalScac || '—'}</Text>
                                      <Text>Service: {c.serviceLevel || '—'}</Text>
                                      <Text>Transit: {c.transitDays ?? '—'} days</Text>
                                      <Text>Delivery: {formatDate(c.deliveryDate)}</Text>
                                    </Grid>
                                  </Box>
                                )
                              })}
                            </Stack>
                          </RadioGroup>
                        </Box>
                      )}

                      {freightResponse.disclaimer && (
                        <Text fontSize="xs" color="gray.500" mt={2}>{freightResponse.disclaimer}</Text>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Flex>
          ) : (
            <Box p={4} bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.200" color="gray.600" fontSize="sm">
              Enter EMC, Cabinet, and/or Pole Cover dimensions above to see crate size and weight estimate.
            </Box>
          )}
        </GridItem>
      </Grid>
    </Box>
  )
}

EdgeCrateResultsForm.propTypes = {
  form: PropTypes.object.isRequired,
  setForm: PropTypes.func
}
